import React, { Component } from "react";

import Header from "../../layout/Header";
import BannerSimple from "../../layout/BannerSimple";
import Footer from "../../layout/Footer";
import SEO from "../../layout/SEO";
import ContactInformation from "../../layout/ContactInformation";
import Loading from "../../layout/Loading";
import ProjectCard from "../../shared/ProjectCard";
import publicApi from "../../../utils/publicApi";

/**
 * /projets — la liste complète des projets publiés.
 *
 * Style historique du front (cf. docs/STYLE_FRONT_LEGACY.md) : même ossature
 * que /article — Header modeDark + BannerSimple + section .section-gap + grille
 * Bootstrap. Les filtres réutilisent les classes .article-filters / .filter-btn
 * déjà stylées dans _articles-grid.scss.
 */
export default class ProjectsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      projects: [],
      loading: true,
      error: null,
      activeFilter: "",
    };
  }

  componentDidMount() {
    window.resetScript();
    this.loadProjects();
  }

  loadProjects = async () => {
    try {
      this.setState({ loading: true, error: null });
      const response = await publicApi.get("/api/projects?limit=100");
      this.setState({ projects: response.data?.projects || [], loading: false });
    } catch (error) {
      console.error("Error loading projects:", error);
      this.setState({
        projects: [],
        loading: false,
        error: "Impossible de charger les projets pour le moment.",
      });
    }
  };

  /** Technologies présentes sur au moins un projet, pour la barre de filtres. */
  getFilters = () => {
    const counts = new Map();

    this.state.projects.forEach((project) => {
      [...(project.technologies || []), ...(project.frameworks || [])].forEach((tech) => {
        counts.set(tech, (counts.get(tech) || 0) + 1);
      });
    });

    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 8)
      .map(([tech]) => tech);
  };

  getVisibleProjects = () => {
    const { projects, activeFilter } = this.state;
    if (!activeFilter) return projects;

    return projects.filter((project) =>
      [...(project.technologies || []), ...(project.frameworks || [])].includes(activeFilter),
    );
  };

  renderFilters = () => {
    const filters = this.getFilters();
    if (filters.length < 2) return null;

    const { activeFilter } = this.state;

    return (
      <div className="row mb-4">
        <div className="col-12">
          <div className="article-filters">
            <h3 className="mb-3">Filtrer par technologie</h3>
            <div className="filter-buttons">
              {["", ...filters].map((filter) => (
                <button
                  key={filter || "all"}
                  type="button"
                  className={`filter-btn ${activeFilter === filter ? "active" : ""}`}
                  aria-pressed={activeFilter === filter}
                  onClick={() => this.setState({ activeFilter: filter })}
                >
                  {filter || "Tous les projets"}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderBody = () => {
    const { loading, error, projects } = this.state;

    if (loading) {
      return (
        <div className="row">
          <div className="col-12">
            <Loading variant="dots" size="medium" color="primary" text="Chargement des projets..." />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="row">
          <div className="col-12">
            <div className="projects-empty">
              <p>{error}</p>
              <button
                type="button"
                className="primary-btn mt-3"
                data-text="Réessayer"
                onClick={this.loadProjects}
              >
                {"Réessayer".split("").map((rows, keys) => <span key={keys}>{rows}</span>)}
              </button>
            </div>
          </div>
        </div>
      );
    }

    const visible = this.getVisibleProjects();

    if (visible.length === 0) {
      return (
        <div className="row">
          <div className="col-12">
            <div className="projects-empty">
              <p>
                {projects.length === 0
                  ? "Aucun projet publié pour le moment."
                  : "Aucun projet avec cette technologie."}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="row">
        {visible.map((project) => (
          <ProjectCard key={project.id} project={project} />
        ))}
      </div>
    );
  };

  render() {
    const { projects, loading } = this.state;

    return (
      <>
        <SEO
          title="Projets"
          description="Les projets que j'ai conçus et développés : contexte, choix techniques, stack et liens."
          keywords="projets, réalisations, développement web, portfolio, stack technique"
        />
        <Header active="Projets" modeDark={true} />

        <BannerSimple
          title="Projets"
          boxElement={[
            { name: "Accueil", link: "/" },
            { name: "Projets", link: "/projets" },
          ]}
        />

        <section className="projects-page-area section-gap">
          <div className="container">
            {!loading && projects.length > 0 && (
              <div className="row">
                <div className="col-12">
                  <p className="projects-count">
                    {projects.length} projet{projects.length > 1 ? "s" : ""}
                  </p>
                </div>
              </div>
            )}

            {this.renderFilters()}
            {this.renderBody()}
          </div>
        </section>

        <ContactInformation />

        <Footer />
      </>
    );
  }
}
